import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import PerformanceTimeline from '../components/PerformanceTimeline';
import WeeklyQuestCard from '../components/WeeklyQuestCard';
import {
  Shield, Swords, Target, Flame, Users, Cog, FileText,
  TrendingUp, TrendingDown, Loader2, Info, X, Lightbulb, RefreshCw,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const CLASS_ICONS = {
  shield: Shield, swords: Swords, target: Target, flame: Flame, users: Users, cog: Cog,
};

// One-tap CTA mapping per pillar: takes the user straight to the section
// referenced in the "Comment l'ameliorer" tip. Friction-zero between
// "see weak spot" and "act on it".
const PILLAR_CTA = {
  vitalite_mouvement: { route: '/workouts', label_fr: 'Aller a Entrainement', label_en: 'Go to Workouts' },
  nutrition_carburant: { route: '/meals', label_fr: 'Aller a Mes Repas', label_en: 'Go to My Meals' },
  recuperation_optimale: { route: '/hydration', label_fr: 'Aller a Hydratation', label_en: 'Go to Hydration' },
  force_mentale: { route: '/coach-ai', label_fr: 'Aller a l\'Assistant IA', label_en: 'Go to AI Assistant' },
  sommeil_equilibre: { route: '/sleep', label_fr: 'Aller a Sommeil', label_en: 'Go to Sleep' },
  // performance_globale = meta-pillar; no direct CTA (user should focus on their weakest)
};

const CLASS_COLORS = {
  stoique: { primary: '#6366F1', glow: 'rgba(99,102,241,0.3)' },
  avant_garde: { primary: '#EF4444', glow: 'rgba(239,68,68,0.3)' },
  pilier: { primary: '#22C55E', glow: 'rgba(34,197,94,0.3)' },
  stratege: { primary: '#3B82F6', glow: 'rgba(59,130,246,0.3)' },
  phoenix: { primary: '#F97316', glow: 'rgba(249,115,22,0.3)' },
  machine: { primary: '#A855F7', glow: 'rgba(168,85,247,0.3)' },
};

// Universal score color (0-10)
const scoreColor = (s) => {
  if (s >= 7) return {
    text: 'text-emerald-300', score: 'text-emerald-200', bg: 'bg-emerald-500/15',
    border: 'border-emerald-500/50', glow: 'rgba(16,185,129,0.25)', hex: '#10B981', label_fr: 'Excellent', label_en: 'Excellent',
  };
  if (s >= 4) return {
    text: 'text-amber-300', score: 'text-amber-200', bg: 'bg-amber-500/15',
    border: 'border-amber-500/50', glow: 'rgba(245,158,11,0.25)', hex: '#F59E0B', label_fr: 'A surveiller', label_en: 'Monitor',
  };
  return {
    text: 'text-red-300', score: 'text-red-200', bg: 'bg-red-500/15',
    border: 'border-red-500/50', glow: 'rgba(239,68,68,0.25)', hex: '#EF4444', label_fr: 'Priorite', label_en: 'Priority',
  };
};

export const MonthlyReport = () => {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [infoPillar, setInfoPillar] = useState(null);
  // Iter. 406.k — per-pillar delta vs previous month (for "+X.X" badges).
  const [monthlyDeltas, setMonthlyDeltas] = useState({});

  const t = (fr, en) => (language === 'fr' ? fr : en);
  const fr = language === 'fr';

  const fetchReport = useCallback(async () => {
    try {
      const res = await api.get('/reports/mission');
      setReport(res.data.report);
    } catch {
      /* no report yet */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Iter. 406.k — Fetch month-over-month pillar deltas once we have a report.
  useEffect(() => {
    if (!report?.month || !report?.year) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/insights/pillar-progression/monthly-delta', {
          params: { month: report.month, year: report.year },
        });
        if (!cancelled) setMonthlyDeltas(res?.data?.deltas || {});
      } catch {
        /* deltas are a UI enhancement — silent failure is fine */
      }
    })();
    return () => { cancelled = true; };
  }, [report?.month, report?.year]);

  const handleGenerate = async ({ force = false } = {}) => {
    setGenerating(true);
    try {
      const now = new Date();
      const res = await api.post(
        `/reports/mission/generate?month=${now.getMonth() + 1}&year=${now.getFullYear()}${force ? '&force=true' : ''}`
      );
      setReport(res.data.report);
      toast.success(force
        ? t('Rapport recalcule avec tes dernieres stats !', 'Report refreshed with your latest stats!')
        : t('Rapport de Mission genere !', 'Mission Report generated!')
      );
    } catch {
      toast.error(t('Erreur de generation', 'Generation error'));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto text-center py-16 px-4" data-testid="monthly-report-empty">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-heading uppercase tracking-tight mb-2">
            {t('Rapport de Mission', 'Mission Report')}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t(
              'Aucun rapport disponible. Genere ton premier bilan mensuel.',
              'No report available. Generate your first monthly assessment.'
            )}
          </p>
          <Button
            onClick={() => handleGenerate()}
            disabled={generating}
            className="font-heading uppercase tracking-wider bg-gradient-to-r from-red-600 to-red-700"
            data-testid="generate-report-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Swords className="w-4 h-4 mr-2" />}
            {generating ? t('Generation...', 'Generating...') : t('Generer le Rapport', 'Generate Report')}
          </Button>
        </div>
      </Layout>
    );
  }

  const classColor = CLASS_COLORS[report.combatant_class?.id] || CLASS_COLORS.stoique;
  const ClassIcon = CLASS_ICONS[report.combatant_class?.icon] || Shield;
  const ai = report.ai_analysis || {};
  // 🎯 6 universal pillars — primary UI source.
  // Fall back to radar_data only if an older cached report is in DB.
  const pillars = Array.isArray(report.pillars) && report.pillars.length
    ? report.pillars
    : (report.radar_data || []).slice(0, 6);

  // Strongest / weakest from the new universal pillars (or legacy fallback)
  const strongest = report.strongest_pillar || report.strongest_principle || {};
  const weakest = report.weakest_pillar || report.weakest_principle || {};

  return (
    <Layout>
      <div className="max-w-2xl mx-auto pb-24 px-4" data-testid="monthly-report-page">
        {/* Header — GLOW (Iter. 411.mm5) */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-4 pt-4">
          <p
            className="text-[10px] uppercase tracking-[0.3em] text-orange-300/80"
            style={{ textShadow: '0 0 6px rgba(251,146,60,0.4)' }}
          >
            {t('Rapport de Mission', 'Mission Report')}
          </p>
          <h1
            className="text-2xl sm:text-3xl font-heading font-black uppercase tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #fbbf24 0%, #fb923c 60%, #ef4444 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 10px rgba(251,146,60,0.4))',
            }}
            data-testid="report-month-title"
          >
            {report.month_label} {report.year}
          </h1>
        </motion.div>

        {/* Refresh button — GLOW */}
        <div className="flex justify-center mb-4">
          <button
            onClick={() => handleGenerate({ force: true })}
            disabled={generating}
            className="text-[11px] uppercase tracking-wider text-orange-200/70 hover:text-amber-300 transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-orange-500/25 hover:border-amber-400/50 bg-black/30 backdrop-blur-md"
            style={{ boxShadow: '0 0 10px rgba(251,146,60,0.15)' }}
            data-testid="refresh-report-btn"
          >
            <RefreshCw className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
            {t('Recalculer avec mes stats actuelles', 'Refresh with my current stats')}
          </button>
        </div>

        {/* Combatant Class Card — GLOW (Iter. 411.mm5) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl p-5 mb-5 relative overflow-hidden transition-all"
          style={{
            border: `1px solid ${classColor.primary}55`,
            background: `linear-gradient(160deg, ${classColor.primary}18 0%, rgba(0,0,0,0.72) 100%)`,
            backdropFilter: 'blur(14px)',
            boxShadow: `0 0 26px ${classColor.glow}, inset 0 1px 0 rgba(255,255,255,0.05)`,
          }}
          data-testid="combatant-class-card"
        >
          <div className="flex items-center gap-4">
            <motion.div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: `${classColor.primary}25`,
                border: `1.5px solid ${classColor.primary}60`,
                boxShadow: `inset 0 0 12px ${classColor.primary}30`,
              }}
              animate={{ boxShadow: [`0 0 15px ${classColor.glow}`, `0 0 32px ${classColor.glow}`, `0 0 15px ${classColor.glow}`] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <ClassIcon
                className="w-8 h-8"
                style={{ color: classColor.primary, filter: `drop-shadow(0 0 6px ${classColor.primary}90)` }}
              />
            </motion.div>
            <div className="flex-1">
              <p
                className="text-[10px] uppercase tracking-wider"
                style={{ color: `${classColor.primary}CC`, textShadow: `0 0 4px ${classColor.glow}` }}
              >
                {t('Classe de Combattant', 'Combatant Class')}
              </p>
              <p
                className="font-heading text-xl font-black uppercase tracking-tight"
                style={{
                  color: classColor.primary,
                  textShadow: `0 0 10px ${classColor.glow}`,
                }}
                data-testid="combatant-class-name"
              >
                {report.combatant_class?.name}
              </p>
              <p className="text-xs text-white/70 mt-1">{report.combatant_class?.description}</p>
            </div>
          </div>
        </motion.div>

        {/* 🎯 6 UNIVERSAL PILLARS GRID — 2 cols × 3 rows */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-1"
        >
          {/* 📈 Performance Timeline (Sparkline) — motivation visuelle */}
          <PerformanceTimeline language={language} />
        </motion.div>

        {/* 🎯 6 UNIVERSAL PILLARS GRID — Centre de Commandement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-5"
          data-testid="pillars-grid-section"
        >
          <h2 className="text-sm font-heading uppercase tracking-wider text-foreground text-center mb-1">
            {t('Tes 6 Piliers du Mois', 'Your 6 Pillars This Month')}
          </h2>
          <p className="text-[10px] text-muted-foreground text-center mb-3">
            {t('Tapote une carte pour voir ton plan d\'action', 'Tap a card to see your action plan')}
          </p>
          <div className="grid grid-cols-2 gap-3" data-testid="pillars-grid">
            {pillars.map((p) => {
              const c = scoreColor(p.score);
              const pid = p.id || p.principle_id;
              const delta = monthlyDeltas[pid];
              const showDelta = delta !== null && delta !== undefined && Math.abs(delta) >= 0.1;
              const isUp = showDelta && delta > 0;
              return (
                <button
                  key={pid}
                  onClick={() => setInfoPillar(p)}
                  className={`relative rounded-2xl p-4 border ${c.border} hover:scale-[1.02] active:scale-[0.98] transition-all text-center backdrop-blur-md`}
                  style={{
                    background: `linear-gradient(160deg, ${c.glow.replace('0.25', '0.14').replace('rgba', 'rgba')} 0%, rgba(0,0,0,0.68) 100%)`,
                    boxShadow: `0 0 22px ${c.glow}, inset 0 1px 0 rgba(255,255,255,0.05)`,
                  }}
                  data-testid={`pillar-${pid}`}
                >
                  <div className="absolute top-2 right-2">
                    <Info className="w-3.5 h-3.5 text-white/40" />
                  </div>
                  {showDelta && (
                    <div
                      className={`absolute top-2 left-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                        isUp
                          ? 'bg-emerald-500/30 text-emerald-200 border border-emerald-400/40'
                          : 'bg-red-500/25 text-red-200 border border-red-400/40'
                      }`}
                      style={{
                        boxShadow: isUp
                          ? '0 0 8px rgba(16,185,129,0.35)'
                          : '0 0 8px rgba(239,68,68,0.30)',
                      }}
                      data-testid={`pillar-delta-${pid}`}
                      aria-label={fr ? `Evolution vs mois dernier` : `Change vs last month`}
                    >
                      {isUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                      {isUp ? '+' : ''}{delta.toFixed(1)}
                    </div>
                  )}
                  <div
                    className="text-3xl mb-2"
                    style={{ filter: `drop-shadow(0 0 8px ${c.hex}55)` }}
                    aria-hidden="true"
                  >
                    {p.icon || '🎯'}
                  </div>
                  <p
                    className={`text-2xl font-heading font-black ${c.score} leading-none mb-1`}
                    style={{ textShadow: `0 0 12px ${c.glow.replace('0.25', '0.5')}` }}
                    data-testid={`pillar-score-${pid}`}
                  >
                    {p.score}<span className={`text-sm ${c.text} font-bold`}>/10</span>
                  </p>
                  <p className="text-[10px] font-heading uppercase tracking-wider text-white/90 leading-tight">
                    {p.name_fr || p.principle}
                  </p>
                  <p
                    className={`text-[9px] uppercase tracking-widest mt-1 font-semibold ${c.text}`}
                    style={{ textShadow: `0 0 6px ${c.glow}` }}
                  >
                    {fr ? c.label_fr : c.label_en}
                  </p>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Point Fort / Faible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5"
        >
          {/* Strongest — GLOW emerald */}
          <div
            className="p-4 rounded-xl transition-all"
            style={{
              border: '1px solid rgba(16,185,129,0.35)',
              background: 'linear-gradient(160deg, rgba(16,185,129,0.14) 0%, rgba(0,0,0,0.68) 100%)',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 0 20px rgba(16,185,129,0.28), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
            data-testid="strongest-pillar-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" style={{ filter: 'drop-shadow(0 0 4px rgba(16,185,129,0.7))' }} />
              <span
                className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold"
                style={{ textShadow: '0 0 6px rgba(16,185,129,0.5)' }}
              >
                {t('Point Fort', 'Strength')}
              </span>
              <span
                className="ml-auto text-xl font-black text-emerald-300"
                style={{ textShadow: '0 0 10px rgba(16,185,129,0.6)' }}
              >
                {strongest.score}/10
              </span>
            </div>
            <p className="text-sm font-heading uppercase text-white mb-2 flex items-center gap-1.5">
              {strongest.icon && (
                <span aria-hidden="true" style={{ filter: 'drop-shadow(0 0 6px rgba(16,185,129,0.5))' }}>
                  {strongest.icon}
                </span>
              )}
              {strongest.name_fr || strongest.name || ''}
            </p>
            {(strongest.description_fr || strongest.description_en) && (
              <p className="text-[11px] text-emerald-100/80 leading-relaxed mb-2">
                {fr ? strongest.description_fr : strongest.description_en}
              </p>
            )}
            {(strongest.action_tip_fr || strongest.action_tip_en) && (
              <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-emerald-500/25">
                <Lightbulb className="w-3 h-3 text-emerald-300 mt-0.5 shrink-0" style={{ filter: 'drop-shadow(0 0 4px rgba(16,185,129,0.7))' }} />
                <p className="text-[10px] text-emerald-100/90 leading-snug">
                  <strong className="uppercase tracking-wide">{t('Continue :', 'Keep going:')}</strong>{' '}
                  {fr ? strongest.action_tip_fr : strongest.action_tip_en}
                </p>
              </div>
            )}
            {PILLAR_CTA[strongest.id] && (
              <button
                type="button"
                onClick={() => navigate(PILLAR_CTA[strongest.id].route)}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/50 text-emerald-100 text-[10px] font-bold uppercase tracking-wider transition-all active:scale-[0.98]"
                style={{ boxShadow: '0 0 12px rgba(16,185,129,0.35)' }}
                data-testid={`strongest-cta-${strongest.id}`}
              >
                <span>{fr ? PILLAR_CTA[strongest.id].label_fr : PILLAR_CTA[strongest.id].label_en}</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Weakest — GLOW red */}
          <div
            className="p-4 rounded-xl transition-all"
            style={{
              border: '1px solid rgba(239,68,68,0.35)',
              background: 'linear-gradient(160deg, rgba(239,68,68,0.14) 0%, rgba(0,0,0,0.68) 100%)',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 0 20px rgba(239,68,68,0.26), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
            data-testid="weakest-pillar-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 text-red-400" style={{ filter: 'drop-shadow(0 0 4px rgba(239,68,68,0.7))' }} />
              <span
                className="text-[10px] uppercase tracking-widest text-red-300 font-bold"
                style={{ textShadow: '0 0 6px rgba(239,68,68,0.5)' }}
              >
                {t('Point Faible', 'Weakness')}
              </span>
              <span
                className="ml-auto text-xl font-black text-red-300"
                style={{ textShadow: '0 0 10px rgba(239,68,68,0.6)' }}
              >
                {weakest.score}/10
              </span>
            </div>
            <p className="text-sm font-heading uppercase text-white mb-2 flex items-center gap-1.5">
              {weakest.icon && (
                <span aria-hidden="true" style={{ filter: 'drop-shadow(0 0 6px rgba(239,68,68,0.5))' }}>
                  {weakest.icon}
                </span>
              )}
              {weakest.name_fr || weakest.name || ''}
            </p>
            {(weakest.description_fr || weakest.description_en) && (
              <p className="text-[11px] text-red-100/80 leading-relaxed mb-2">
                {fr ? weakest.description_fr : weakest.description_en}
              </p>
            )}
            {(weakest.action_tip_fr || weakest.action_tip_en) && (
              <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-red-500/25">
                <Target className="w-3 h-3 text-red-300 mt-0.5 shrink-0" style={{ filter: 'drop-shadow(0 0 4px rgba(239,68,68,0.7))' }} />
                <p className="text-[10px] text-red-100/90 leading-snug">
                  <strong className="uppercase tracking-wide">{t('Action :', 'Action:')}</strong>{' '}
                  {fr ? weakest.action_tip_fr : weakest.action_tip_en}
                </p>
              </div>
            )}
            {PILLAR_CTA[weakest.id] && (
              <button
                type="button"
                onClick={() => navigate(PILLAR_CTA[weakest.id].route)}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-red-50 text-[11px] font-bold uppercase tracking-wider transition-all active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, rgba(239,68,68,0.9) 0%, rgba(220,38,38,0.95) 100%)',
                  border: '1px solid rgba(239,68,68,0.6)',
                  boxShadow: '0 0 16px rgba(239,68,68,0.5), inset 0 1px 0 rgba(255,255,255,0.15)',
                }}
                data-testid={`weakest-cta-${weakest.id}`}
              >
                <span>{fr ? PILLAR_CTA[weakest.id].label_fr : PILLAR_CTA[weakest.id].label_en}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </motion.div>

        {/* AI Analysis — GLOW amber */}
        {ai.monthly_analysis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl p-5 mb-5 transition-all"
            style={{
              border: '1px solid rgba(251,191,36,0.35)',
              background: 'linear-gradient(160deg, rgba(251,191,36,0.10) 0%, rgba(0,0,0,0.72) 100%)',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 0 20px rgba(251,191,36,0.22), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
            data-testid="ai-analysis-section"
          >
            <h3
              className="text-xs font-heading uppercase tracking-wider text-amber-200 mb-3"
              style={{ textShadow: '0 0 6px rgba(251,191,36,0.5)' }}
            >
              {t('Analyse YUMIX', 'YUMIX Analysis')}
            </h3>
            <p className="text-sm text-amber-50/95 leading-relaxed">{ai.monthly_analysis}</p>
          </motion.div>
        )}
        {report.narrative_stale && !ai.monthly_analysis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 mb-5 flex items-start gap-3"
            data-testid="narrative-stale-notice"
          >
            <RefreshCw className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-300 mb-1">
                {t('Analyse a regenerer', 'Analysis needs regeneration')}
              </p>
              <p className="text-xs text-amber-200/80 leading-relaxed">
                {t(
                  "Tu as logue beaucoup d'actions depuis la derniere generation de ce rapport. Les scores sont a jour, mais le texte d'analyse est obsolete. Genere une nouvelle analyse pour refleter ta progression reelle.",
                  "You've logged a lot of actions since this report was generated. The scores are current but the analysis text is outdated. Generate a new analysis to reflect your real progress."
                )}
              </p>
            </div>
          </motion.div>
        )}

        {/* Ordre de Marche */}
        {ai.ordre_de_marche && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="rounded-2xl p-5 mb-5 border-2 border-red-500/30 bg-gradient-to-br from-red-500/10 to-transparent"
            data-testid="ordre-de-marche"
          >
            <h3 className="text-xs font-heading uppercase tracking-wider text-red-400 mb-3 flex items-center gap-2">
              <Target className="w-3.5 h-3.5" />
              {t('Ordre de Marche — Mois Suivant', 'March Order — Next Month')}
            </h3>
            <p className="text-sm text-foreground/90 leading-relaxed font-medium">{ai.ordre_de_marche}</p>
          </motion.div>
        )}

        {/* 🗓️ Défi 7 Jours — Iter. 406.fr.3 — unified colorful interactive
            weekly quest, shared with the Dashboard. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mb-5"
          data-testid="weekly-quest-section"
        >
          <WeeklyQuestCard language={fr ? 'fr' : 'en'} />
        </motion.div>

        {/* Tactical Quote */}
        {ai.tactical_quote && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-center p-4 border-t border-border/30">
            <p className="text-sm italic text-muted-foreground leading-relaxed">&quot;{ai.tactical_quote}&quot;</p>
            <p className="text-[10px] text-muted-foreground/60 mt-1">— Assistant YUMIX</p>
          </motion.div>
        )}

        {/* Stats Summary */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="grid grid-cols-4 gap-2 mt-4" data-testid="monthly-stats-grid">
          {[
            { label: t('Repas', 'Meals'), value: report.stats?.meals || 0, color: 'text-orange-400' },
            { label: t('Workouts', 'Workouts'), value: report.stats?.workouts || 0, color: 'text-violet-400' },
            { label: t('Eau', 'Water'), value: report.stats?.water_logs || 0, color: 'text-cyan-400' },
            { label: t('Jours', 'Days'), value: report.stats?.active_days || 0, color: 'text-emerald-400' },
          ].map(s => (
            <div key={s.label} className="text-center p-3 rounded-xl bg-card/30 border border-border/30">
              <p className={`text-lg font-black ${s.color}`}>{s.value}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Per-Pillar Info Modal — explains the metric + how to influence it */}
      {infoPillar && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
          onClick={() => setInfoPillar(null)}
          data-testid="pillar-info-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/60 rounded-2xl max-w-md w-full p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl ${scoreColor(infoPillar.score).bg} ${scoreColor(infoPillar.score).border} border`} aria-hidden="true">
                {infoPillar.icon || '🎯'}
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {t('Pilier', 'Pillar')} · {infoPillar.score}/10
                </p>
                <h2 className="font-heading text-base uppercase tracking-tight text-foreground leading-tight">
                  {infoPillar.name_fr || infoPillar.principle || infoPillar.name}
                </h2>
              </div>
              <button
                onClick={() => setInfoPillar(null)}
                className="text-muted-foreground hover:text-foreground p-1"
                data-testid="close-pillar-info"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div data-testid="pillar-info-description">
                <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1 flex items-center gap-1.5">
                  <Info className="w-3 h-3" />
                  {t('Ce que ca mesure', 'What it measures')}
                </p>
                <p className="text-xs text-foreground/85 leading-relaxed">
                  {fr
                    ? (infoPillar.description_fr || infoPillar.description_en || '')
                    : (infoPillar.description_en || infoPillar.description_fr || '')}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="pillar-info-action-tip">
                <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1 flex items-center gap-1.5">
                  <Lightbulb className="w-3 h-3" />
                  {t("Comment l'ameliorer", 'How to improve')}
                </p>
                <p className="text-xs text-amber-100/95 leading-relaxed">
                  {fr
                    ? (infoPillar.action_tip_fr || infoPillar.action_tip_en || '')
                    : (infoPillar.action_tip_en || infoPillar.action_tip_fr || '')}
                </p>
                {(() => {
                  const cta = PILLAR_CTA[infoPillar.id];
                  if (!cta) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => { setInfoPillar(null); navigate(cta.route); }}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-amber-950 text-[11px] font-bold uppercase tracking-wider transition-all active:scale-[0.98] shadow-lg shadow-amber-500/20"
                      data-testid={`pillar-cta-${infoPillar.id}`}
                    >
                      <span>{fr ? cta.label_fr : cta.label_en}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  );
                })()}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </Layout>
  );
};

export default MonthlyReport;
